export enum DesignStyle {
    SITE_THEME = 'site-theme',
    BASIC = 'basic',
}

export enum PricingType {
    FREE = 'FREE',
    PAID = 'PAID',
}

export interface FeatureMetadata {
    isNativeBookingAvailable: boolean;
    isNativeBookingPublishingAvailable: boolean;
    isPaidNativeBookingAvailable: boolean;
}

export interface PriceInfo {
    pricingType: PricingType;
    basePrice: number | null;
    formattedBasePrice: string | null;
    formattedTaxIncludedPrice: string | null;
    taxIncludedPrice: number | null;
    taxPercentage: number | null;
    taxIncluded: boolean;
}

export interface BookerContextType extends FeatureMetadata, PriceInfo {
    eventSlug: string;
    teamId: number;
    userName: string;
    rescheduleUid?: string | null;
    cancelBookingId?: string | null;
    widgetId: string;
    designStyle: DesignStyle;
    language: string;
    apiUrl: string;
    clientId: string;
    organizationId: number;
    eventIdentifier: string;
    isBookingDryRun: boolean;
}

export interface Booking {
    title: string;
    userPrimaryEmail: string;
    startTime: string;
    user: {
        name: string;
    };
    videoCallUrl?: string;
    location?: string;
    uid: string;
    endTime: Date;
    attendees: Array<{
        name: string;
        email: string;
    }>;
    status: BookingStatus;
    metadata?: Record<string, string>;
}

export interface CreateBookingInput {
    eventTypeId: number;
    timeZone: string;
    language: string;
    metadata: Record<string, string>;
    responses: Record<string, string | { value: string; optionValue: string }>;
    slot_duration: number;
    start: string;
    end: string;
}

export enum UrlAction {
    RESCHEDULE = 'reschedule',
    CANCEL = 'cancel',
}

export enum BookingStatus {
    CANCELLED = 'CANCELLED',
    PENDING = 'PENDING',
    RESCHEDULED = 'RESCHEDULED',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
}

export interface CalOrganization {
    id: number;
    name: string;
    slug: string;
    bannerUrl: string | null;
}

export interface CalUserProfile {
    id: number | null;
    upId: string;
    username: string;
    organizationId: number | null;
    organization: CalOrganization | null;
}

export interface CalUser {
    username: string;
    name: string;
    avatarUrl: string | null;
    weekStart: string;
    organizationId: number | null;
    bookerUrl: string;
    image?: string;
    brandColor?: string | null;
    darkBrandColor?: string | null;
    theme?: string | null;
    bookerLayouts?: CalBookerLayouts | null;
}

export interface CalUserWithProfile extends CalUser {
    profile: CalUserProfile;
}

export interface CalHost {
    user: {
        username: string;
    };
}

export interface CalEntity {
    fromRedirectOfNonOrgLink: boolean;
    considerUnpublished: boolean;
    orgSlug: string;
    teamSlug: string;
    name: string;
    hideProfileLink: boolean;
    logoUrl: string;
}

export interface CalBookerLayouts {
    id: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
}

export interface CalParent {
    slug: string;
    name: string;
    bannerUrl: string;
    logoUrl: string;
}

export interface CalSchedule {
    id: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
}

export interface CalLocation {
    type: string;
    [key: string]: any;
}

export interface CalCustomInput {
    [key: string]: any;
}

export interface CalMetadata {
    disableStandardEmails?: {
        all?: {
            host?: boolean;
        };
    };
    apps?: {
        [appName: string]: {
            enabled?: boolean;
            thankYouPage?: string;
            [key: string]: any;
        };
    };
    [key: string]: any;
}

export interface CalFieldTranslation {
    [key: string]: any;
}

export interface CalRecurringEvent {
    [key: string]: any;
}

export interface CalBookingFieldSource {
    id: string;
    type: string;
    label: string;
}

export interface CalBookingFieldVariant {
    name: string;
    type: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
}

export interface CalBookingFieldVariantsConfig {
    variants: {
        [variantName: string]: {
            fields: CalBookingFieldVariant[];
        };
    };
}

export interface CalBookingFieldOptionsInputs {
    [optionName: string]: {
        type: string;
        required?: boolean;
        placeholder?: string;
    };
}

export interface CalBookingFieldView {
    label: string;
    id: string;
}

export interface CalBookingField {
    name: string;
    type: string;
    defaultLabel: string;
    defaultPlaceholder?: string;
    required: boolean;
    hidden?: boolean;
    variant?: string;
    variantsConfig?: CalBookingFieldVariantsConfig;
    editable?: string;
    sources: CalBookingFieldSource[];
    disableOnPrefill?: boolean;
    getOptionsAt?: string;
    optionsInputs?: CalBookingFieldOptionsInputs;
    hideWhenJustOneOption?: boolean;
    views?: CalBookingFieldView[];
}

export interface CalTeamParent {
    slug: string;
    name: string;
    bannerUrl: string | null;
    logoUrl: string | null;
}

export interface CalTeam {
    parentId?: number;
    metadata?: any;
    brandColor?: string | null;
    darkBrandColor?: string | null;
    slug: string;
    name: string;
    logoUrl?: string | null;
    theme?: string | null;
    hideTeamProfileLink?: boolean;
    parent?: CalTeamParent;
    isPrivate?: boolean;
}

export interface CalWorkflow {
    [key: string]: any;
}

export interface CalOwner {
    [key: string]: any;
}

export interface CalInstantMeetingParameter {
    [key: string]: any;
}

export interface CalAiPhoneCallConfig {
    [key: string]: any;
}

export interface CalSubsetOfHostUser {
    id: number;
    avatarUrl: string | null;
    username: string;
    name: string;
    weekStart: string;
    brandColor: string | null;
    darkBrandColor: string | null;
    theme: string | null;
    metadata: any;
    organization: CalOrganization;
    defaultScheduleId: number;
    nonProfileUsername: string;
    profile: CalUserProfile;
}

export interface CalSubsetOfHost {
    user: CalSubsetOfHostUser;
}

export interface CalEventData {
    id: number;
    title: string;
    description: string;
    interfaceLanguage: string | null;
    eventName: string | null;
    slug: string;
    isInstantEvent: boolean;
    instantMeetingParameters: CalInstantMeetingParameter[];
    aiPhoneCallConfig: CalAiPhoneCallConfig | null;
    schedulingType: string;
    length: number;
    locations: CalLocation[];
    customInputs: CalCustomInput[];
    disableGuests: boolean;
    metadata: CalMetadata;
    lockTimeZoneToggleOnBookingPage: boolean;
    lockedTimeZone: string | null;
    requiresConfirmation: boolean;
    autoTranslateDescriptionEnabled: boolean;
    fieldTranslations: CalFieldTranslation[];
    requiresBookerEmailVerification: boolean;
    recurringEvent: CalRecurringEvent | null;
    price: number;
    currency: string;
    seatsPerTimeSlot: number | null;
    disableCancelling: boolean;
    disableRescheduling: boolean;
    allowReschedulingCancelledBookings: boolean;
    seatsShowAvailabilityCount: boolean;
    bookingFields: CalBookingField[];
    teamId: number;
    team: CalTeam;
    successRedirectUrl: string | null;
    forwardParamsSuccessRedirect: boolean;
    workflows: CalWorkflow[];
    owner: CalOwner | null;
    schedule: CalSchedule | null;
    instantMeetingSchedule: CalSchedule | null;
    periodType: string;
    periodDays: number | null;
    periodEndDate: string | null;
    periodStartDate: string | null;
    periodCountCalendarDays: boolean | null;
    hidden: boolean;
    assignAllTeamMembers: boolean;
    rescheduleWithSameRoundRobinHost: boolean;
    parent: CalParent | null;
    subsetOfHosts: CalSubsetOfHost[];
    bookerLayouts: CalBookerLayouts | null;
    profile: CalUser;
    subsetOfUsers: CalUserWithProfile[];
    entity: CalEntity;
    isDynamic: boolean;
    showInstantEventConnectNowModal: boolean;
}

export interface BookerStoreValues {
    state: string;
}
